// ═══════════════════════════════════════════════════════════════
// PIPELINE STAGE COLUMN — Collapsible column in the pipeline board
// Path: src/components/cultivation/PipelineStageColumn.jsx
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { getPipelineAction } from "../../utils/cultivationUtils";
import { PipelineCard } from "./PipelineCard";

export const PipelineStageColumn = ({
  stage,
  stageColors,
  members,
  palette,
  onMemberClick,
  collapsed,
  onToggleCollapse,
}) => {
  const sorted = [...members].sort((a, b) => {
    const urgOrder = { overdue: 0, soon: 1, good: 2 };
    const aAction = getPipelineAction(a, a.engagement);
    const bAction = getPipelineAction(b, b.engagement);
    const urgDiff = urgOrder[aAction.urgency] - urgOrder[bAction.urgency];
    if (urgDiff !== 0) return urgDiff;
    const aDays = a.engagement.daysSinceContact;
    const bDays = b.engagement.daysSinceContact;
    if (aDays === null && bDays !== null) return -1;
    if (bDays === null && aDays !== null) return 1;
    return (bDays || 0) - (aDays || 0);
  });

  const overdueCount = sorted.filter(
    (m) => getPipelineAction(m, m.engagement).urgency === "overdue"
  ).length;

  // ── Collapsed State ──────────────────────────────────────────
  if (collapsed) {
    return (
      <div
        onClick={onToggleCollapse}
        style={{
          width: 56,
          minWidth: 56,
          maxWidth: 56,
          display: "flex",
          flexDirection: "column",
          borderRadius: 8,
          overflow: "hidden",
          background: stageColors.columnBg,
          border: `1px solid ${stageColors.borderColor}`,
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.width = "62px";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.width = "56px";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <div
          style={{
            background: stageColors.headerBg,
            padding: "10px 4px",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ChevronRight size={ICON_SIZES.sm} style={{ opacity: 0.7 }} />
          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>
            {members.length}
          </div>
          {overdueCount > 0 && (
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                background: "#dc2626",
                color: "#fff",
                padding: "1px 4px",
                borderRadius: 4,
                lineHeight: 1.2,
              }}
            >
              {overdueCount}!
            </div>
          )}
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px 0",
          }}
        >
          <span
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              transform: "rotate(180deg)",
              fontSize: 13,
              fontWeight: 700,
              color: stageColors.color,
              letterSpacing: 0.5,
              fontFamily: FONT_FAMILY,
              whiteSpace: "nowrap",
            }}
          >
            {stage.label}
          </span>
        </div>
      </div>
    );
  }

  // ── Expanded State ───────────────────────────────────────────
  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 260,
        maxWidth: 340,
        display: "flex",
        flexDirection: "column",
        borderRadius: 8,
        overflow: "hidden",
        background: stageColors.columnBg,
        border: `1px solid ${stageColors.borderColor}`,
        transition: "all 0.2s ease",
      }}
    >
      {/* Column Header — clickable to collapse */}
      <div
        onClick={onToggleCollapse}
        style={{
          background: stageColors.headerBg,
          padding: "12px 14px",
          color: "#fff",
          cursor: "pointer",
          transition: "opacity 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          <span
            style={{
              fontWeight: 800,
              fontSize: 20,
              letterSpacing: 0.3,
              fontFamily: FONT_FAMILY,
            }}
          >
            {stage.label}
          </span>
          <ChevronDown
            size={ICON_SIZES.sm}
            style={{ opacity: 0.6 }}
            title="Click to collapse"
          />
        </div>
        <div
          style={{
            fontSize: 12,
            opacity: 0.9,
            fontWeight: 500,
            lineHeight: 1.3,
            marginBottom: 2,
          }}
        >
          {stage.instruction}
        </div>
        <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 600 }}>
          {overdueCount > 0 ? (
            <span>
              <span
                style={{
                  display: "inline-block",
                  background: "#dc2626",
                  color: "#fff",
                  padding: "0px 5px",
                  borderRadius: 6,
                  fontSize: 10,
                  fontWeight: 800,
                  marginRight: 4,
                }}
              >
                {overdueCount}
              </span>
              need attention · {members.length} total
            </span>
          ) : (
            <span>
              {members.length}{" "}
              {stage.key === "prospect" ? "prospect" : "donor"}
              {members.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Cards */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "6px 6px",
          minHeight: 80,
        }}
      >
        {sorted.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "24px 12px",
              color: "#9ca3af",
              fontSize: 13,
              fontStyle: "italic",
            }}
          >
            {stage.key === "prospect"
              ? "No prospects yet"
              : "No donors at this stage"}
          </div>
        ) : (
          sorted.map((m) => (
            <PipelineCard
              key={m.id}
              member={m}
              engagement={m.engagement}
              stage={stage}
              stageColors={stageColors}
              palette={palette}
              onClick={onMemberClick}
            />
          ))
        )}
      </div>
    </div>
  );
};
